import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUser, faTag, faClock, faSearch, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { blogPosts } from './Blogdata';
import SEO from '../components/SEO';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex ;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    
    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 2rem;
  animation: ${fadeUp} 1s ease-out;
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  position: relative;
  z-index: 1;
`;

const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  gap: 2rem;
`;

const BlogCard = styled.article`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeUp} 0.8s ease-out;
  animation-delay: ${props => props.index * 0.1}s;
  opacity: 0;
  animation-fill-mode: forwards;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const BlogImage = styled.div`
  height: 250px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.5), transparent);
  }
`;

const BlogContent = styled.div`
  padding: 2rem;
`;

const BlogTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const BlogMeta = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1rem;
  color: #666;
  font-size: 0.9rem;
  
  div {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const BlogExcerpt = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ReadMoreButton = styled.button`
  background: transparent;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const Sidebar = styled.aside`
  position: sticky;
  top: 2rem;
  height: fit-content;
`;

const SearchBox = styled.div`
  background: white;
  border-radius: 15px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
`;

const SearchInput = styled.div`
  position: relative;
  
  input {
    width: 100%;
    padding: 1rem 1rem 1rem 3rem;
    border: 2px solid #eee;
    border-radius: 25px;
    font-size: 1rem;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }
  
  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #666;
  }
`;

const CategoryBox = styled(SearchBox)`
  h3 {
    font-size: 1.2rem;
    margin-bottom: 1rem;
    color: ${props => props.theme.colors.text};
  }
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  
  li {
    margin-bottom: 0.5rem;
    
    button {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      color: #666;
      padding: 0.5rem;
      border-radius: 5px;
      transition: all 0.3s ease;
      border: none;
      background: none;
      cursor: pointer;
      text-align: left;
      
      &:hover {
        background: #f5f5f5;
        color: ${props => props.theme.colors.primary};
      }
      
      span {
        font-size: 0.9rem;
        color: #999;
      }
    }
  }
`;

const getCategories = (posts) => {
  const categoryCount = posts.reduce((acc, post) => {
    acc[post.category] = (acc[post.category] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(categoryCount).map(([name, count]) => ({
    name,
    count
  }));
};

const BlogPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const categories = getCategories(blogPosts);


  const filteredPosts = blogPosts
    .filter(post => 
      post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(post => 
      activeCategory === "All" || post.category === activeCategory
    );

  return (
    <PageWrapper>
      <SEO
        title="Blog | The Big Heart Association Morocco | Stories of Impact"
        description="Read stories of impact, education, and solidarity from The Big Heart Association. Follow our Heartbeat Journey across rural Morocco and discover how every action counts."
        url="/blog"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Blog", url: "/blog" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>Our <span>Blog</span></PageTitle>
          <PageSubtitle>Stay updated with our latest news, stories, and insights about our mission and impact.</PageSubtitle>
        </PageHeroContent>
      </PageHero>
      <MainContent>
        <BlogContainer>
          <BlogGrid>
            {filteredPosts.map((post, index) => (
              <BlogCard key={post.id} index={index}>
                <BlogImage image={post.image} />
                <BlogContent>
                  <BlogMeta>
                    <div>
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      {post.date}
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faUser} />
                      {post.author}
                    </div>
                    <div>
                      <FontAwesomeIcon icon={faClock} />
                      {post.readTime}
                    </div>
                  </BlogMeta>
                  <BlogTitle>{post.title}</BlogTitle>
                  <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                  <Link to={`/blog/${post.id}`} style={{ textDecoration: 'none' }}>
                    <ReadMoreButton>
                      Read More &nbsp;<FontAwesomeIcon icon={faArrowRight} />
                    </ReadMoreButton>
                  </Link>
                </BlogContent>
              </BlogCard>
            ))}
          </BlogGrid>
          <Sidebar>
            <SearchBox>
              <SearchInput>
                <FontAwesomeIcon icon={faSearch} />
                <input 
                  type="text" 
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchInput>
            </SearchBox>
            <CategoryBox>
              <h3>Categories</h3>
              <CategoryList>
                <li key="all">
                  <button onClick={() => setActiveCategory("All")}>
                    <div>
                      <FontAwesomeIcon icon={faTag} />
                      {" "}All
                    </div>
                    <span>({blogPosts.length})</span>
                  </button>
                </li>
                {categories.map(category => (
                  <li key={category.name}>
                    <button 
                      onClick={() => setActiveCategory(category.name)}
                      style={{
                        color: activeCategory === category.name ? props => props.theme.colors.primary : '#666'
                      }}
                    >
                      <div>
                        <FontAwesomeIcon icon={faTag} />
                        {" "}{category.name}
                      </div>
                      <span>({category.count})</span>
                    </button>
                  </li>
                ))}
              </CategoryList>
            </CategoryBox>
          </Sidebar>
        </BlogContainer>
      </MainContent>
    </PageWrapper>
  );
};

export default BlogPage;